import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import Breadcrumb from '../../../shared/ui/Breadcrumb';
import LoadingSpinner from '../../../shared/ui/LoadingSpinner';
import LoginPrompt from '../../../shared/ui/LoginPrompt';
import ToggleSwitch from '../../../shared/ui/ToggleSwitch';
import {
  addAccountsToRanking,
  checkUserPowerAtGuild,
  fetchMutualGuilds,
  fetchSpecificFlexQRanking,
  fetchSpecificRanking,
  removeAccountsFromRanking
} from '../../../services/api';
import RankTierIcon from './components/RankTierIcon';
import './RankingDetailsPage.css';

const TIER_SCORE = {
  UNRANKED: 0,
  IRON: 1,
  BRONZE: 2,
  SILVER: 3,
  GOLD: 4,
  PLATINUM: 5,
  EMERALD: 6,
  DIAMOND: 7,
  MASTER: 8,
  GRANDMASTER: 9,
  CHALLENGER: 10
};

const DIVISION_SCORE = {
  NONE: 0,
  IV: 1,
  III: 2,
  II: 3,
  I: 4
};

const QUEUE_TYPE = {
  SOLO: 'soloQ',
  FLEX: 'flexQ'
};

const ACCOUNT_BATCH_LIMIT = 10;

function extractEnumValue(value) {
  if (typeof value === 'string') {
    return value.trim().toUpperCase();
  }

  if (value && typeof value === 'object') {
    if (typeof value.value === 'string') {
      return value.value.trim().toUpperCase();
    }
    if (typeof value.name === 'string') {
      return value.name.trim().toUpperCase();
    }
  }

  return '';
}

function getLeaguePointsValue(rawRank, account) {
  if (Number.isFinite(rawRank?.leaguePoints)) {
    return rawRank.leaguePoints;
  }
  if (Number.isFinite(rawRank?.lp)) {
    return rawRank.lp;
  }
  if (Number.isFinite(account?.leaguePoints)) {
    return account.leaguePoints;
  }
  return 0;
}

function getWinrateValue(rawRank, account) {
  let source = {};

  if (rawRank?.winrate && typeof rawRank.winrate === 'object') {
    source = rawRank.winrate;
  } else if (account?.winrate && typeof account.winrate === 'object') {
    source = account.winrate;
  }

  const wins = Number.isFinite(source.wins) ? source.wins : 0;
  let losses = 0;
  if (Number.isFinite(source.losses)) {
    losses = source.losses;
  } else if (Number.isFinite(source.defeats)) {
    losses = source.defeats;
  }

  return {wins, losses};
}

function getAccountRank(account) {
  const rawRank = account?.rank && typeof account.rank === 'object'
      ? account.rank
      : {};

  const tier = extractEnumValue(rawRank.tier || account?.tier);
  const division = extractEnumValue(rawRank.division || account?.division);
  const leaguePoints = getLeaguePointsValue(rawRank, account);
  const winrate = getWinrateValue(rawRank, account);

  return {
    tier,
    division,
    leaguePoints,
    winrate
  };
}

function decodeRouteParam(value = '') {
  return decodeURIComponent(value);
}

function getRankScore(account) {
  const rank = getAccountRank(account);
  const tierScore = TIER_SCORE[rank.tier] || 0;
  const divisionScore = DIVISION_SCORE[rank.division] || 0;
  const lp = Number.isFinite(rank.leaguePoints) ? rank.leaguePoints : 0;
  return tierScore * 10000 + divisionScore * 1000 + lp;
}

function compareByEloDescending(accountA, accountB) {
  return getRankScore(accountB) - getRankScore(accountA);
}

function formatRank(account) {
  const rank = getAccountRank(account);
  if (!rank?.tier) {
    return 'Unranked';
  }

  const parts = [rank.tier];
  if (rank.division && rank.division !== 'NONE') {
    parts.push(rank.division);
  }

  if (Number.isFinite(rank.leaguePoints)) {
    parts.push(`${rank.leaguePoints} LP`);
  }

  return parts.join(' ');
}

function getWinrateSummary(account) {
  const winrate = getAccountRank(account).winrate;
  const wins = Number.isFinite(winrate?.wins) ? winrate.wins : 0;
  const losses = Number.isFinite(winrate?.losses) ? winrate.losses : 0;
  const totalGames = wins + losses;
  if (totalGames === 0) {
    return {label: 'No games', detail: '0W / 0L'};
  }

  const percentage = Math.round((wins / totalGames) * 100);
  return {
    label: `${percentage}% WR`,
    detail: `${wins}W / ${losses}L`
  };
}

function formatQueueLabel(queueType) {
  if (queueType === QUEUE_TYPE.FLEX) {
    return 'FlexQ';
  }
  return 'SoloQ';
}

async function fetchRankingByQueue(guildId, rankingId, queueType) {
  if (queueType === QUEUE_TYPE.FLEX) {
    return fetchSpecificFlexQRanking(guildId, rankingId);
  }
  return fetchSpecificRanking(guildId, rankingId);
}

function createDraftAccount() {
  return {
    id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: '',
    tagLine: ''
  };
}

export default function RankingDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {guildId: guildIdParam, rankingId: rankingIdParam} = useParams();

  const guildId = decodeRouteParam(guildIdParam || '');
  const rankingId = decodeRouteParam(rankingIdParam || '');

  const [guildName, setGuildName] = useState(location.state?.guildName || '');
  const [ranking, setRanking] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(QUEUE_TYPE.SOLO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasGuildPower, setHasGuildPower] = useState(false);
  const [draftAccounts, setDraftAccounts] = useState([]);
  const [pendingRemovals, setPendingRemovals] = useState(new Set());
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const guildPowerCacheRef = useRef({});

  const rankingDisplayName = ranking?.id || ranking?.name || rankingId;

  const sortedAccounts = useMemo(() => {
    if (!ranking?.accounts || !Array.isArray(ranking.accounts)) {
      return [];
    }

    return [...ranking.accounts].sort(compareByEloDescending);
  }, [ranking]);

  const breadcrumbItems = useMemo(() => {
    const items = [{label: 'Ranky', path: '/'}];

    if (guildName) {
      items.push({label: guildName, path: `/guilds?guildId=${guildId}`});
    } else if (guildId) {
      items.push({label: 'Server', path: `/guilds?guildId=${guildId}`});
    }

    items.push({label: rankingDisplayName || 'Ranking', isCurrent: true});
    return items;
  }, [guildId, guildName, rankingDisplayName]);

  const handleBreadcrumbBack = () => {
    navigate(`/guilds?guildId=${guildId}`);
  };

  const handleEditModeToggle = () => {
    setIsEditMode(previousMode => {
      const nextMode = !previousMode;
      if (!nextMode) {
        setDraftAccounts([]);
        setPendingRemovals(new Set());
        setEditError('');
        setEditSuccess('');
      }
      return nextMode;
    });
  };

  const getAccountKey = (account) =>
      `${(account?.name || '').trim().toLowerCase()}#${(account?.tagLine
          || account?.tag || '').trim().toLowerCase()}`;

  const handleToggleRemoval = (account) => {
    setEditError('');
    setEditSuccess('');
    setPendingRemovals(previous => {
      const next = new Set(previous);
      const key = getAccountKey(account);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleAddDraftRow = () => {
    setEditError('');
    setEditSuccess('');
    setDraftAccounts(
        previousDrafts => [...previousDrafts, createDraftAccount()]);
  };

  const handleRemoveDraftRow = (draftId) => {
    setDraftAccounts(previousDrafts =>
        previousDrafts.filter(draft => draft.id !== draftId)
    );
  };

  const handleDraftChange = (draftId, field, value) => {
    setEditError('');
    setEditSuccess('');
    setDraftAccounts(previousDrafts =>
        previousDrafts.map(draft =>
            draft.id === draftId ? {...draft, [field]: value} : draft
        )
    );
  };

  const handleUpdateRanking = async () => {
    if (!guildId || !rankingId || !isEditMode || !hasGuildPower
        || isSubmittingEdit) {
      return;
    }

    const hasDrafts = draftAccounts.length > 0;
    const hasRemovals = pendingRemovals.size > 0;

    if (!hasDrafts && !hasRemovals) {
      return;
    }

    // Validate draft rows
    const invalidRowNumbers = [];
    const existingAccountKeys = new Set(
        sortedAccounts.map(account => getAccountKey(account))
    );
    const draftAccountKeys = new Set();
    const parsedAccounts = [];

    draftAccounts.forEach((draftAccount, draftIndex) => {
      const name = draftAccount.name.trim();
      const tagLine = draftAccount.tagLine.trim();

      if (!name || !tagLine) {
        invalidRowNumbers.push(draftIndex + 1);
        return;
      }

      const uniqueKey = `${name.toLowerCase()}#${tagLine.toLowerCase()}`;
      if (existingAccountKeys.has(uniqueKey) || draftAccountKeys.has(
          uniqueKey)) {
        return;
      }

      draftAccountKeys.add(uniqueKey);
      parsedAccounts.push({name, tagLine});
    });

    if (invalidRowNumbers.length > 0) {
      setEditSuccess('');
      setEditError(
          `Complete both fields for row(s): ${invalidRowNumbers.join(', ')}.`);
      return;
    }

    setEditError('');
    setEditSuccess('');
    setIsSubmittingEdit(true);

    try {
      // Remove marked accounts first
      if (hasRemovals) {
        const accountsToRemove = sortedAccounts
        .filter(account => pendingRemovals.has(getAccountKey(account)))
        .map(account => ({
          name: account.name,
          tagLine: account.tagLine || account.tag || ''
        }));

        for (let start = 0; start < accountsToRemove.length;
            start += ACCOUNT_BATCH_LIMIT) {
          await removeAccountsFromRanking(guildId, rankingId,
              accountsToRemove.slice(start, start + ACCOUNT_BATCH_LIMIT));
        }
      }

      // Add new draft accounts
      if (parsedAccounts.length > 0) {
        for (let start = 0; start < parsedAccounts.length;
            start += ACCOUNT_BATCH_LIMIT) {
          await addAccountsToRanking(guildId, rankingId,
              parsedAccounts.slice(start, start + ACCOUNT_BATCH_LIMIT));
        }
      }

      const addedCount = parsedAccounts.length;
      const removedCount = pendingRemovals.size;
      const parts = [];
      if (removedCount > 0) {
        parts.push(`${removedCount} account(s) removed`);
      }
      if (addedCount > 0) {
        parts.push(`${addedCount} account(s) added`);
      }

      setDraftAccounts([]);
      setPendingRemovals(new Set());
      setEditSuccess(parts.join(', ') + '.');
      setRefreshTick(previousTick => previousTick + 1);
    } catch (err) {
      setEditError(err?.message || 'Error updating ranking.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  useEffect(() => {
    if (!guildId) {
      setHasGuildPower(false);
      return;
    }

    if (isEditMode) {
      return;
    }

    const cachedGuildPower = guildPowerCacheRef.current[guildId];
    if (typeof cachedGuildPower === 'boolean') {
      setHasGuildPower(cachedGuildPower);
      return;
    }

    let isCancelled = false;

    const checkGuildPower = async () => {
      try {
        const hasPowerResult = await checkUserPowerAtGuild(guildId);
        if (isCancelled) {
          return;
        }

        const hasPower = Boolean(hasPowerResult);
        guildPowerCacheRef.current[guildId] = hasPower;
        setHasGuildPower(hasPower);
      } catch (err) {
        if (!isCancelled) {
          guildPowerCacheRef.current[guildId] = false;
          setHasGuildPower(false);
        }
      }
    };

    checkGuildPower();

    return () => {
      isCancelled = true;
    };
  }, [guildId, isEditMode]);

  useEffect(() => {
    if (!guildId || !rankingId) {
      setRanking(null);
      setError('');
      setEditError('');
      setEditSuccess('');
      setDraftAccounts([]);
      setPendingRemovals(new Set());
      setIsEditMode(false);
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const loadDetails = async () => {
      setLoading(true);
      setError('');
      setRequiresLogin(false);
      setEditError('');
      setEditSuccess('');

      try {
        const rankingResult = await fetchRankingByQueue(guildId, rankingId,
            selectedQueue);

        if (isCancelled) {
          return;
        }

        setRanking(rankingResult);

        try {
          const mutualGuilds = await fetchMutualGuilds();

          if (isCancelled) {
            return;
          }

          const selectedGuild = Array.isArray(mutualGuilds)
              ? mutualGuilds.find(guild => guild.id === guildId)
              : null;
          setGuildName(selectedGuild?.name || location.state?.guildName ||
              '');
        } catch (guildError) {
          // Ranking visibility is decided by backend; guild name enrichment is optional.
          setGuildName(location.state?.guildName || '');
        }
      } catch (err) {
        if (!isCancelled) {
          setRanking(null);
          if (err?.status === 401) {
            setRequiresLogin(true);
          } else {
            setError(err?.message || `Error loading ${formatQueueLabel(
                selectedQueue)} ranking details.`);
          }
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      isCancelled = true;
    };
  }, [guildId, location.state?.guildName, rankingId, selectedQueue,
    refreshTick]);

  if (!guildId || !rankingId) {
    return (
        <div className="ranking-details-page ranking-details-state">
          Select a ranking from the server rankings page.
        </div>
    );
  }

  if (requiresLogin) {
    return (
        <section className="ranking-details-page">
          <div className="ranking-details-breadcrumb-nav">
            <button
                className="ranking-details-back-arrow"
                onClick={handleBreadcrumbBack}
                title="Back to server rankings"
                aria-label="Back to server rankings"
            >
              &lsaquo;
            </button>
            <Breadcrumb items={breadcrumbItems}/>
          </div>
          <LoginPrompt
              message="This ranking is private. You need to be logged in to view it."/>
        </section>
    );
  }

  if (loading) {
    return (
        <section className="ranking-details-page">
          <div className="ranking-details-breadcrumb-nav">
            <button
                className="ranking-details-back-arrow"
                onClick={handleBreadcrumbBack}
                title="Back to server rankings"
                aria-label="Back to server rankings"
            >
              &lsaquo;
            </button>
            <Breadcrumb items={breadcrumbItems}/>
            <div className="ranking-queue-toggle"
                 aria-label="Ranked queue toggle">
              <button
                  type="button"
                  className={`ranking-queue-toggle-button ${selectedQueue
                  === QUEUE_TYPE.SOLO
                      ? 'is-active' : ''}`}
                  onClick={() => setSelectedQueue(QUEUE_TYPE.SOLO)}
                  aria-pressed={selectedQueue === QUEUE_TYPE.SOLO}
              >
                SoloQ
              </button>
              <button
                  type="button"
                  className={`ranking-queue-toggle-button ${selectedQueue
                  === QUEUE_TYPE.FLEX
                      ? 'is-active' : ''}`}
                  onClick={() => setSelectedQueue(QUEUE_TYPE.FLEX)}
                  aria-pressed={selectedQueue === QUEUE_TYPE.FLEX}
              >
                FlexQ
              </button>
            </div>
          </div>
          <div className="ranking-details-loading">
            <LoadingSpinner message={`Loading ${formatQueueLabel(
                selectedQueue)} ranking...`}/>
          </div>
        </section>
    );
  }

  return (
      <section className="ranking-details-page">
        <div className="ranking-details-breadcrumb-nav">
          <button
              className="ranking-details-back-arrow"
              onClick={handleBreadcrumbBack}
              title="Back to server rankings"
              aria-label="Back to server rankings"
          >
            &lsaquo;
          </button>
          <Breadcrumb items={breadcrumbItems}/>
          <div className="ranking-details-top-controls">
            {hasGuildPower && (
                <div className="ranking-details-edit-toggle-anchor">
                  <ToggleSwitch
                      label="Edit mode"
                      checked={isEditMode}
                      onChange={handleEditModeToggle}
                  />
                </div>
            )}
            <div className="ranking-queue-toggle"
                 aria-label="Ranked queue toggle">
              <button
                  type="button"
                  className={`ranking-queue-toggle-button ${selectedQueue
                  === QUEUE_TYPE.SOLO
                      ? 'is-active' : ''}`}
                  onClick={() => setSelectedQueue(QUEUE_TYPE.SOLO)}
                  aria-pressed={selectedQueue === QUEUE_TYPE.SOLO}
              >
                SoloQ
              </button>
              <button
                  type="button"
                  className={`ranking-queue-toggle-button ${selectedQueue
                  === QUEUE_TYPE.FLEX
                      ? 'is-active' : ''}`}
                  onClick={() => setSelectedQueue(QUEUE_TYPE.FLEX)}
                  aria-pressed={selectedQueue === QUEUE_TYPE.FLEX}
              >
                FlexQ
              </button>
            </div>
          </div>
        </div>

        <section className="ranking-details-leaderboard"
                 aria-label="Ranking accounts">

          {!error && (sortedAccounts.length > 0 || (isEditMode
              && hasGuildPower)) && (
              <ul className={`ranking-details-list${isEditMode && hasGuildPower
                  ? ' ranking-details-list--edit' : ''}`}>
                {sortedAccounts.map((account, index) => {
                  const rank = getAccountRank(account);
                  const winrateSummary = getWinrateSummary(account);
                  const isPendingDelete = pendingRemovals.has(
                      getAccountKey(account));
                  return (
                      <li
                          key={`${account.id || account.name
                          || 'account'}-${index}`}
                          className={`ranking-account-card${isPendingDelete
                              ? ' ranking-account-card--pending-delete' : ''}`}
                      >
                        <div className="ranking-account-position">{index
                            + 1}</div>
                        <div className="ranking-account-profile">
                          <h2>{account.name || 'Unknown summoner'}</h2>
                          <p>{account.tagLine || account.tag || '-'}</p>
                        </div>
                        <div className="ranking-account-league">
                          <RankTierIcon tier={rank.tier}/>
                          <div>
                            <p className="ranking-account-rank">{formatRank(
                                account)}</p>
                            <p className="ranking-account-lp">{`${rank.leaguePoints} LP`}</p>
                          </div>
                        </div>
                        <div className="ranking-account-winrate">
                          <p>{winrateSummary.label}</p>
                          <span>{winrateSummary.detail}</span>
                        </div>
                        {isEditMode && hasGuildPower && (
                            <div className="ranking-account-row-action">
                              <button
                                  type="button"
                                  className={`ranking-account-remove-button${isPendingDelete
                                      ? ' is-active' : ''}`}
                                  onClick={() => handleToggleRemoval(account)}
                                  disabled={isSubmittingEdit}
                                  aria-label="Mark for removal"
                              >
                                ×
                              </button>
                            </div>
                        )}
                      </li>
                  );
                })}

                {isEditMode && hasGuildPower && draftAccounts.map(
                    (draftAccount, draftIndex) => (
                        <li key={draftAccount.id}
                            className="ranking-account-card ranking-account-draft-row">
                          <div className="ranking-account-position">
                            {sortedAccounts.length + draftIndex + 1}
                          </div>
                          <div
                              className="ranking-account-profile ranking-account-draft-inputs">
                            <input
                                type="text"
                                className="ranking-draft-input"
                                placeholder="Game name"
                                value={draftAccount.name}
                                onChange={(event) => handleDraftChange(
                                    draftAccount.id, 'name',
                                    event.target.value)}
                                disabled={isSubmittingEdit}
                            />
                            <input
                                type="text"
                                className="ranking-draft-input ranking-draft-input-tag"
                                placeholder="#TagLine"
                                value={draftAccount.tagLine}
                                onChange={(event) => handleDraftChange(
                                    draftAccount.id, 'tagLine',
                                    event.target.value)}
                                disabled={isSubmittingEdit}
                            />
                          </div>
                          <div className="ranking-account-league"/>
                          <div className="ranking-account-winrate"/>
                          <div className="ranking-account-row-action">
                            <button
                                type="button"
                                className="ranking-account-remove-button"
                                onClick={() => handleRemoveDraftRow(
                                    draftAccount.id)}
                                disabled={isSubmittingEdit}
                                aria-label="Remove row"
                            >
                              ×
                            </button>
                          </div>
                        </li>
                    ))}

                {isEditMode && hasGuildPower && (
                    <li className="ranking-account-add-row-item">
                      <button
                          type="button"
                          className="ranking-account-add-row-button"
                          onClick={handleAddDraftRow}
                          disabled={isSubmittingEdit}
                          aria-label="Add account row"
                      >
                        +
                      </button>
                    </li>
                )}
              </ul>
          )}

          {isEditMode && hasGuildPower && !error && (
              <div className="ranking-draft-footer">
                {(draftAccounts.length > 0 || pendingRemovals.size > 0) && (
                    <button
                        type="button"
                        className="ranking-details-edit-add-button"
                        onClick={handleUpdateRanking}
                        disabled={isSubmittingEdit}
                    >
                      {isSubmittingEdit ? 'Updating...' : 'Update ranking'}
                    </button>
                )}
                {editError && (
                    <p className="ranking-details-state ranking-details-error">{editError}</p>
                )}
                {!editError && editSuccess && (
                    <p className="ranking-details-state ranking-details-success">{editSuccess}</p>
                )}
              </div>
          )}
        </section>

        {error && (
            <p className="ranking-details-state ranking-details-error">{error}</p>
        )}

        {!error && sortedAccounts.length === 0 && !(isEditMode && hasGuildPower)
            && (
            <p className="ranking-details-state">
              No accounts found in this ranking.
            </p>
        )}
      </section>
  );
}


